import { useEffect, useRef } from 'react';
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { PARTNER_API_AVAILABLE, fetchProofPayload, verifyProof } from '../partner/api';
import { readToken, removeToken, writeToken } from '../partner/session';
import { linkTelegramNotifications, NOTIFY_ENABLED } from '../ton/notify';
import { isTelegramMiniApp } from '../ton/referral';

/**
 * Links the connected wallet to the Telegram notification bot as soon as an
 * address is available (so the bot knows the user's wallet even before their
 * first ticket), and once more when the user becomes registered on-chain (so
 * the bot can resolve their referral key). Fires at most once per
 * address/registration state. Disabled entirely unless `VITE_NOTIFY_API_BASE`
 * is configured.
 */
export function useNotifyLink(address: string | null | undefined, registered: boolean): void {
  const wallet = useTonWallet();
  const [tonConnectUI] = useTonConnectUI();
  const linkedFor = useRef<{ address: string; registered: boolean } | null>(null);
  const desired = useRef('');
  const proofToken = useRef<{ address: string; promise: Promise<string | null> } | null>(null);
  const inFlight = useRef<{ key: string; promise: Promise<void> } | null>(null);

  useEffect(() => {
    if (!NOTIFY_ENABLED || !PARTNER_API_AVAILABLE || !isTelegramMiniApp() || wallet) return;
    let cancelled = false;
    tonConnectUI.setConnectRequestParameters({ state: 'loading' });
    void fetchProofPayload()
      .then((payload) => {
        if (!cancelled) {
          tonConnectUI.setConnectRequestParameters({
            state: 'ready',
            value: { tonProof: payload },
          });
        }
      })
      .catch(() => {
        if (!cancelled) tonConnectUI.setConnectRequestParameters(null);
      });
    return () => {
      cancelled = true;
    };
  }, [wallet, tonConnectUI]);

  useEffect(() => {
    const key = address && wallet ? `${address}:${registered ? 'registered' : 'connected'}` : '';
    desired.current = key;
    if (!NOTIFY_ENABLED || !isTelegramMiniApp() || !address || !wallet) return;
    const prev = linkedFor.current;
    if (prev && prev.address === address && (prev.registered || !registered)) return;
    if (inFlight.current?.key === key) return;

    const getToken = (): Promise<string | null> => {
      if (!PARTNER_API_AVAILABLE) return Promise.resolve(null);
      const stored = readToken(address);
      if (stored) return Promise.resolve(stored);
      if (proofToken.current?.address === address) return proofToken.current.promise;
      const item = wallet.connectItems?.tonProof;
      if (!item || !('proof' in item)) return Promise.resolve(null);
      const account = wallet.account;
      if (!account.publicKey || !account.walletStateInit) return Promise.resolve(null);
      const promise = verifyProof({
        address: account.address,
        publicKey: account.publicKey,
        walletStateInit: account.walletStateInit,
        proof: item.proof,
      })
        .then((token) => {
          writeToken(address, token, false);
          return token;
        })
        .catch(() => null);
      proofToken.current = { address, promise };
      return promise;
    };

    const promise = (async () => {
      const token = await getToken();
      if (desired.current !== key) return;
      const waits = [0, 1_000, 3_000, 8_000];
      for (const wait of waits) {
        if (desired.current !== key) return;
        if (wait > 0) await new Promise((resolve) => window.setTimeout(resolve, wait));
        if (desired.current !== key) return;
        const result = await linkTelegramNotifications(address, token ?? undefined);
        if (result === 'skipped') return;
        if (result === 'linked') {
          if (desired.current === key) linkedFor.current = { address, registered };
          return;
        }
        if (result === 'unauthorized') {
          if (token) {
            removeToken(address);
            const fallback = await linkTelegramNotifications(address);
            if (fallback === 'linked' && desired.current === key) {
              linkedFor.current = { address, registered };
            }
          }
          return;
        }
      }
    })().finally(() => {
      if (inFlight.current?.key === key) inFlight.current = null;
    });
    inFlight.current = { key, promise };
  }, [address, registered, wallet]);
}
